// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from '@emotion/styled';
import { bool, array, func, string } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { uniqueId } from 'lodash';
import {
  Loading,
  SkeletonPlaceholder,
  SkeletonText,
} from '@carbon/react';

import { AnalyticsLink, NWNotification } from 'Components/shared';

import XlsIcon from 'Assets/images/xls.svg';
import CsvIcon from 'Assets/images/csv.svg';
import PdfIcon from 'Assets/images/pdf.svg';
import HtmlIcon from 'Assets/images/html.svg';
import XmlIcon from 'Assets/images/xml.svg';
import JsonIcon from 'Assets/images/json.svg';

const StyledFeeds = styled.div`
  .feedUpdateNotification {
    .message p {
      display: flex;
      align-items: center;
      width: 100%;

      span + span {
        margin-left: 24px;
        padding-left: 24px;
        border-left: 1px solid #A8B3C2;
      }
    }
  }

  section {
    margin-bottom: 16px;
    border: 1px solid #e2e2e2;

    h4 {
      font-size: 16px;
      padding: 16px;
      background: #f5f7f9;
    }

    .feedSection {
      padding: 16px;
      display: flex;
      align-items: center;
      gap: 16px;
      border-bottom: 1px solid #e2e2e2;

      .feedTitle {
        min-width: 250px;
      }

      .feedInfo {
        display: flex;
        flex: auto;
        gap: 16px;

        button:not(.cds--btn) {
          position: relative;
          background: transparent;
          border: none;
          padding: 0;
          cursor: pointer;
  
          svg:not(.cds--loading__svg) {
            width: 40px;
            height: 50px;
          }

          .cds--loading {
            position: absolute;
            top: 18px;
            left: 12px;
          }
        }

        button.cds--btn {
          width: 100%;
          max-width: 255px;
          height: 40px !important;
          background-color: #da1e28;

          svg {
            width: 16px;
          }
        }
      }
    }

    .file {
      display: flex;
      align-items: center;
      padding: 16px;
      border-bottom: 1px solid #e2e2e2;

      .fileIcon {
        margin-right: 16px;

        svg {
          width: 40px;
          height: 50px;
        }
      }

      .fileName {
        &.skeleton {
          flex: 1;
        }

        .fileTitle {
          button {
            background: transparent;
            border: none;
            padding: 0;
            font-size: 16px;
            color: #3a3a3a;
            cursor: pointer;
          }

          a {
            word-break: break-all;
            color: #3a3a3a;
          }
        }

        .fileDate {
          margin-top: 4px;

          span {
            color: #707070;
          }
        }
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .feedUpdateNotification .message p {
      flex-direction: column;
      align-items: flex-start;

      span + span {
        margin: 8px 0 0;
        padding: 8px 0 0;
        border-top: 1px solid #A8B3C2;
        border-left: 0;
      }
    }

    section {
      .feedSection {
        flex-direction: column;
        align-items: start;

        .feedInfo {
          width: 100%;

          button.cds--btn {
            max-width: none;
            width: 100%;
          }
        }
      }

      .file .fileName .fileTitle button {
        text-align: left;
        word-break: break-all;
      }
    }
  }
`;

const StyledSkeletonPlaceholder = styled(SkeletonPlaceholder)`
  height: 3rem;
  width: 3rem;
`;

const Feeds = ({
  feeds,
  loading,
  handleDownload,
  handleFeedDownload,
  loadingFeed,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const renderFileIcon = type => {
    switch (type) {
      case 'xls':
        return <XlsIcon />;
      case 'csv':
        return <CsvIcon />;
      case 'pdf':
        return <PdfIcon />;
      default:
        return <HtmlIcon />;
    }
  };

  return (
    <StyledFeeds theme={ theme }>
      <NWNotification
        className="feedUpdateNotification"
        errors={ [{
          type: 'info',
          message: `<span>${ t('profile.feedUpdateFrequency') }</span><span>${ t('profile.feedPriceStockUpdate') }</span>`
        }] }
      />
      { loading ? Array.from({ length: 2 }, () => (
        <section key={ uniqueId('section') }>
          <h4><SkeletonText /></h4>
          { Array.from({ length: 3 }, () => (
            <div className="file" key={ uniqueId('feeds') }>
              <div className="fileIcon">
                <StyledSkeletonPlaceholder />
              </div>
              <div className="fileName skeleton">
                <SkeletonText paragraph lineCount={ 2 } />
              </div>
            </div>
          ))}
        </section>
      )) : (
        <>
          <section>
            <h4>{ t('profile.downloadFeeds') }</h4>
            <div className="sectionBody">
              <div className="feedSection">
                <div className="feedTitle">{ t('profile.onlyNODProducts') }</div>
                <div className="feedInfo">
                  <button type="button" onClick={ () => handleFeedDownload({ format: 'xml' }) }>
                    { loadingFeed === 'xml-all' && <Loading active small withOverlay={ false } /> }
                    <XmlIcon />
                  </button>
                  <button type="button" onClick={ () => handleFeedDownload({ format: 'json' }) }>
                    { loadingFeed === 'json-all' && <Loading active small withOverlay={ false } /> }
                    <JsonIcon />
                  </button>
                  <button type="button" onClick={ () => handleFeedDownload({ format: 'csv' }) }>
                    { loadingFeed === 'csv-all' && <Loading active small withOverlay={ false } /> }
                    <CsvIcon />
                  </button>
                </div>
              </div>
              <div className="feedSection">
                <div className="feedTitle">{ t('profile.onlySupplierProducts') }</div>
                <div className="feedInfo">
                  <button type="button" onClick={ () => handleFeedDownload({ format: 'xml', isSupplier: true }) }>
                    { loadingFeed === 'xml-all-supplier' && <Loading active small withOverlay={ false } /> }
                    <XmlIcon />
                  </button>
                  <button type="button" onClick={ () => handleFeedDownload({ format: 'json', isSupplier: true }) }>
                    { loadingFeed === 'json-all-supplier' && <Loading active small withOverlay={ false } /> }
                    <JsonIcon />
                  </button>
                  <button type="button" onClick={ () => handleFeedDownload({ format: 'csv', isSupplier: true }) }>
                    { loadingFeed === 'csv-all-supplier' && <Loading active small withOverlay={ false } /> }
                    <CsvIcon />
                  </button>
                </div>
              </div>
              <div className="feedSection">
                <div className="feedTitle">{ t('profile.onlyResealedProducts') }</div>
                <div className="feedInfo">
                  <button type="button" onClick={ () => handleFeedDownload({ format: 'xml', onlyResealed: true }) }>
                    { loadingFeed === 'xml-resealed' && <Loading active small withOverlay={ false } /> }
                    <XmlIcon />
                  </button>
                  <button type="button" onClick={ () => handleFeedDownload({ format: 'json', onlyResealed: true }) }>
                    { loadingFeed === 'json-resealed' && <Loading active small withOverlay={ false } /> }
                    <JsonIcon />
                  </button>
                  <button type="button" onClick={ () => handleFeedDownload({ format: 'csv', onlyResealed: true }) }>
                    { loadingFeed === 'csv-resealed' && <Loading active small withOverlay={ false } /> }
                    <CsvIcon />
                  </button>
                </div>
              </div>
            </div>
          </section>
          <section>
            <h4>{ t('profile.newsletterPrices') }</h4>
            <div className="sectionBody">
              { feeds.map(item => (
                <div className="file" key={ item?.uploadedInformationId }>
                  <div className="fileIcon">
                    { renderFileIcon(item?.type) }
                  </div>
                  <div className="fileName">
                    <div className="fileTitle">
                      { item?.fileName ? (
                        <button
                          type="button"
                          onClick={ () => handleDownload(`/account/info/${ item?.uploadedInformationId }`) }
                        >
                          { item?.fileName || item?.linkName }
                        </button>
                      ) : (
                        <AnalyticsLink
                          to={ item?.linkUrl }
                          target="_blank"
                          event={ { action: 'linked_clicked' } }
                        >
                          { item?.linkName }
                        </AnalyticsLink>
                      ) }
                    </div>
                    <div className="fileDate"><span>{ t('profile.updatedAt') }: </span>{ item?.updatedAt }</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </StyledFeeds>
  );
};

Feeds.propTypes = {
  feeds: array,
  loading: bool,
  handleDownload: func,
  handleFeedDownload: func,
  loadingFeed: string,
};

Feeds.displayName = 'FeedsComponent';

export default Feeds;
